import { Spotlight_Options } from '../../types/spotlight'

/**
 * Steuert den Spotlight-Modus der Anwendung.
 * Singleton – Initialisierung nur in app.ts.
 */
export class Spotlight_Controller {
    private static instance: Spotlight_Controller | null = null

    /** Aktuelles Overlay-Element. */
    private overlay: HTMLDivElement | null = null

    /** @private Verhindert externe Instanziierung. */
    private constructor() {}

    /**
     * Erstellt die Singleton-Instanz und aktiviert den Modus.
     *
     * @throws {Error} Wenn bereits initialisiert
     */
    public static init(): Spotlight_Controller {
        if ( Spotlight_Controller.instance ) {
            throw new Error( 'Spotlight_Controller wurde bereits initialisiert.' )
        }
        Spotlight_Controller.instance = new Spotlight_Controller()
        return Spotlight_Controller.instance
    }

    /**
     * Gibt die bestehende Instanz zurück.
     *
     * @throws {Error} Wenn noch nicht initialisiert
     */
    public static getInstance(): Spotlight_Controller {
        if ( ! Spotlight_Controller.instance ) {
            throw new Error( 'Spotlight_Controller wurde noch nicht initialisiert.' )
        }
        return Spotlight_Controller.instance
    }

    /**
     * Zeigt den Spotlight-Overlay auf einem Element.
     */
    public show( el: HTMLElement, options: { padding?: number, radius?: number, color?: string } = {} ): void {
        this.hide()
        this.scroll_to( el, () => {
            this.overlay = this.create_overlay( el, options )
            document.body.appendChild( this.overlay )
            requestAnimationFrame( () => {
                requestAnimationFrame( () => {
                    if ( this.overlay ) {
                        this.overlay.style.opacity = '1'
                    }
                } )
            } )
        } )
    }

    /**
     * Scrollt sanft zu einem Element und ruft danach einen Callback auf.
     */
    private scroll_to( el: HTMLElement, callback: () => void ): void {
        const target = el.getBoundingClientRect().top + window.scrollY - ( window.innerHeight / 2 )
        const distance = Math.abs( target - window.scrollY )
        const duration = Math.min( Math.max( distance * 0.5, 300 ), 800 )
        const start = window.scrollY
        const start_time = performance.now()

        const tick = ( now: number ) => {
            const progress = Math.min( ( now - start_time ) / duration, 1 )
            const ease = progress < 0.5 ?
                2 * progress * progress :
                -1 + ( 4 - 2 * progress ) * progress

            window.scrollTo( 0, start + ( target - start ) * ease )

            if ( progress < 1 ) {
                requestAnimationFrame( tick )
            } else {
                callback()
            }
        }

        requestAnimationFrame( tick )
    }

    /**
     * Blendet den Spotlight-Overlay aus und entfernt ihn aus dem DOM.
     */
    public hide(): void {
        if ( ! this.overlay ) {
            return
        }
        this.overlay.style.opacity = '0'
        this.overlay.addEventListener(
            'transitionend',
            () => {
                this.overlay?.remove()
                this.overlay = null
            },
            { once: true }
        )
    }

    /**
     * Erstellt das Overlay-Element mit SVG-Maske für das gegebene Element.
     */
    private create_overlay( el: HTMLElement, options: Spotlight_Options = {} ): HTMLDivElement {
        const { padding = 8, radius = 8, color = 'rgba(0,0,0,0.7)' } = options
        const r = el.getBoundingClientRect()
        const t = r.top + window.scrollY - padding
        const l = r.left + window.scrollX - padding
        const b = r.bottom + window.scrollY + padding
        const ri = r.right + window.scrollX + padding
        const W = document.body.scrollWidth
        const H = document.body.scrollHeight

        const div = document.createElement( 'div' )
        div.id = 'spotlight-overlay'
        div.style.width = `${W}px`
        div.style.height = `${H}px`

        div.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">
            <defs>
                <mask id="spotlight-mask">
                    <rect width="${W}" height="${H}" fill="white"/>
                    <rect x="${l}" y="${t}" width="${ri - l}" height="${b - t}" rx="${radius}" fill="black"/>
                </mask>
            </defs>
            <rect width="${W}" height="${H}" fill="${color}" mask="url(#spotlight-mask)"/>
        </svg>`

        div.addEventListener( 'click', () => this.hide() )

        return div
    }
}
